import {
  BodyCopy,
  Header,
  LayoutContainer,
  Maps,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { Property, PropertyWalkScoreData, getDictionaryEntry } from '@greystar/lib';
import { PropertyDetailsMapProps } from '@greystar/ui';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { JSX } from 'react';
import { useI18n } from 'next-localization';

const PropertyDetailsMap = (props: PropertyDetailsMapProps): JSX.Element => {
  const { t } = useI18n();

  if (!props.data?.propertyLocation?.latitude || !props.data?.propertyLocation?.longitude) {
    return <></>;
  }

  const transformWalkScore = (scores: PropertyWalkScoreData | undefined) => {
    if (!scores || (!scores.walkscore && !scores.bikescore && !scores.transitscore)) {
      return undefined;
    }

    const currentScores = [];

    if (scores.walkscore) {
      currentScores.push({
        title: props.fields.walkScoreTitle.value ?? 'Walk Score',
        score: scores.walkscore.toString(),
      });
    }

    if (scores.bikescore) {
      currentScores.push({
        title: props.fields.bikeScoreTitle.value ?? 'Transit Score',
        score: scores.bikescore.toString(),
      });
    }

    if (scores.transitscore) {
      currentScores.push({
        title: props.fields.transitScoreTitle.value ?? 'Bike Score',
        score: scores.transitscore.toString(),
      });
    }

    if (currentScores.length === 0) {
      return undefined;
    }

    return {
      scores: currentScores,
      //link: `https://www.walkscore.com/score/${props.data.propertyLocation?.address}-${props.data.propertyLocation?.city}-${props.data.propertyLocation?.state}`,
      link: 'https://www.walkscore.com/how-it-works/',
    };
  };
  const walkScore =
    props.data.showScores && props.data.scores ? transformWalkScore(props.data.scores) : undefined;

  return (
    <PageSection>
      <PageSectionContent hasMaxWidth className="pt-4 md:pt-0">
        <LayoutContainer flexDirection="column" gap="lg">
          <LayoutContainer flexDirection="column">
            <Header component="h4" as="h2" className="mb-2">
              {props.fields.mapTitle.value}
            </Header>
            <BodyCopy size="medium">
              <span className="map-subtitile">{props.data.propertyName}</span>
              {'  '}
              <span>
                {props.data.propertyLocation?.address +
                  ', ' +
                  props.data.propertyLocation?.city +
                  ', ' +
                  props.data.propertyLocation.state +
                  ', ' +
                  props.data.propertyLocation.postalCode}
              </span>
            </BodyCopy>
          </LayoutContainer>
          <Maps
            mapEmbedClassName="maps-cdp"
            apiKey={process.env.NEXT_PUBLIC_GoogleApiKey ?? ''}
            mapID={process.env.NEXT_PUBLIC_GoogleMapID ?? ''}
            zoom={16}
            mapCenter={{
              lat: props.data.propertyLocation.latitude,
              lng: props.data.propertyLocation.longitude,
            }}
            showPropertyAtCenter={true}
            markers={[]}
            mapsExpandButton={false}
            disableZoom={true}
            isMapControls={true}
            clickableIcons={false}
            walkScoreData={walkScore}
            recenterButtonLabel={getDictionaryEntry(t('map-recenter'), 'Recenter Map')}
            zoomInButtonLabel={getDictionaryEntry(t('map-zoom-in'), 'Zoom In Map')}
            zoomOutButtonLabel={getDictionaryEntry(t('map-zoom-out'), 'Zoom Out Map')}
            noResults={false}
            noResultsText={''}
            noResultsAdditionalText={''}
          />
        </LayoutContainer>
      </PageSectionContent>
    </PageSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsMapProps> => {
  const props = rendering as unknown as PropertyDetailsMapProps;
  const property = layoutData.sitecore.context.property as Property;

  if (!property?.location) {
    return {
      ...props,
      data: {
        propertyName: null,
        propertyLocation: null,
        scores: null,
        notFound: true,
        showScores: false,
      },
      isPageEditing: layoutData.sitecore.context.pageEditing,
    };
  }

  return {
    ...props,
    data: {
      propertyName: property.name ?? null,
      propertyLocation: property?.location ?? null,
      scores: property.walkscoreData ?? null,
      notFound: false,
      showScores: Boolean(property.walkscoreDisplay),
    },
    isPageEditing: layoutData.sitecore.context.pageEditing,
  };
};

export default withDatasourceCheck()<PropertyDetailsMapProps>(PropertyDetailsMap);
